import { useState, useEffect, useCallback } from "react";
import { AlertTriangle, CheckCircle2, XCircle, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const j = async (path, opts = {}) => {
  const r = await fetch(`${API}${path}`, { credentials: "include", ...opts });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
  return data;
};

const Pill = ({ ok, children }) => (
  <span
    data-testid="status-pill"
    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
      ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
    }`}
  >
    {ok ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
    {children}
  </span>
);

export const SystemStatusPanel = () => {
  const [s, setS] = useState(null);
  const [err, setErr] = useState(null);
  const [log, setLog] = useState([]);
  const [envChoice, setEnvChoice] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [msg, setMsg] = useState(null);
  const [gov, setGov] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const st = await j("/admin-console/system-status");
      setS(st);
      setEnvChoice(st.environment.value);
      const lg = await j("/admin-console/email-send-log?limit=25");
      setLog(lg.events || []);
      const g = await j("/admin-console/model-governance");
      setGov(g);
    } catch (e) {
      setErr(e.message);
    }
  }, []);

  const runModelTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await j("/admin-console/model-test", { method: "POST" });
      setTestResult(r.results || []);
      await load();
    } catch (e) { setTestResult([{ purpose: "error", error: e.message }]); }
    setTesting(false);
  };

  useEffect(() => { load(); }, [load]);

  const changeEnv = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await j("/admin-console/environment", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ environment: envChoice, confirm }),
      });
      if (r.no_op) {
        setMsg(r.message || "No change.");
      } else {
        setMsg(`Environment persisted to ${r.audit.to} by ${r.audit.changed_by} (durable). External email now: ${r.audit.enables_external_email ? "ENABLED" : "blocked"}.`);
      }
      setConfirm(false);
      await load();
    } catch (e) { setMsg(`Failed: ${e.message}`); }
    setBusy(false);
  };

  const doPreview = async () => {
    setBusy(true); setMsg(null); setPreview(null);
    try {
      const r = await j("/admin-console/daily-jobs/morning_orchestrator/rerun", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode: "preview" }),
      });
      setPreview(r);
    } catch (e) { setMsg(`Preview failed: ${e.message}`); }
    setBusy(false);
  };

  const doSend = async () => {
    if (!window.confirm(`Re-run and SEND today's morning briefing now? The true outcome (sent / redirected / suppressed) will be shown and recorded in the send log.`)) return;
    setBusy(true); setMsg(null);
    try {
      const r = await j("/admin-console/daily-jobs/morning_orchestrator/rerun", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode: "send" }),
      });
      setMsg(r.outcome || "Re-run complete — see the send log below.");
      setPreview(null);
      await load();
    } catch (e) { setMsg(`Send failed: ${e.message}`); }
    setBusy(false);
  };

  if (err) return <div data-testid="system-status-error" className="text-sm text-red-600">Failed to load status: {err}</div>;
  if (!s) return <div className="text-sm text-slate-500">Loading system status…</div>;

  const emailOk = s.email.enabled;
  const prod = s.environment.is_production;

  return (
    <div data-testid="system-status-panel" className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">System Status</h2>
        <button data-testid="status-refresh" onClick={load} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900">
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      {/* Live state */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="rounded-xl border border-slate-200 p-4 bg-white">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Environment</div>
          <div className="flex items-center gap-2" data-testid="status-environment">
            <span className="text-xl font-bold text-slate-900">{s.environment.value}</span>
            <Pill ok={prod}>{prod ? "production" : "non-production"}</Pill>
            <span
              data-testid="env-persistence-badge"
              className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${s.environment.persistent ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-900"}`}>
              {s.environment.persistent ? "durable" : "temporary"}
            </span>
          </div>
          <div className="text-xs text-slate-500 mt-1">Source: {s.environment.source}</div>
          {s.environment.source_kind === "stored" && s.environment.stored_provenance && (
            <div className="text-[11px] text-slate-400 mt-0.5" data-testid="env-stored-provenance">
              Persisted by: {s.environment.stored_provenance.source}
              {s.environment.stored_provenance.at ? ` at ${s.environment.stored_provenance.at}` : ""}
            </div>
          )}
          {s.environment.deploy_env_value && s.environment.source_kind !== "env_var" && (
            <div className="text-[11px] text-slate-400 mt-0.5">Deploy variable present: {s.environment.deploy_env_value}</div>
          )}
          {s.environment.persistence_note && (
            <div className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 mt-2" data-testid="env-persistence-note">
              ⚠ {s.environment.persistence_note}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 p-4 bg-white">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Outbound Email</div>
          <div data-testid="status-email"><Pill ok={emailOk}>{emailOk ? "enabled" : "blocked"}</Pill></div>
          <div className="text-xs text-slate-600 mt-2">{s.email.reason}</div>
          <div className="text-xs text-slate-500 mt-1">
            Allowlist: {s.email.allowlist_count} address(es) — {s.email.allowlist_behaviour}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 p-4 bg-white">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Prompt & Classifier</div>
          <div className="text-sm text-slate-800" data-testid="status-prompt">Variant: <b>{s.prompt.active_variant ?? "—"}</b></div>
          <div className="text-sm text-slate-800">Classifier: <b>{s.prompt.classifier_enabled ? "on" : "off"}</b></div>
        </div>

        <div className="rounded-xl border border-slate-200 p-4 bg-white">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Models in service</div>
          <div className="text-sm text-slate-800 space-y-0.5" data-testid="status-models">
            {s.models.by_tier && Object.entries(s.models.by_tier).map(([k, v]) => {
              const drifted = typeof v === "object" && v.drifted;
              return (
                <div key={k} className="flex items-center gap-2 flex-wrap">
                  <span>{k}: <b>{typeof v === "object" ? v.label : v}</b></span>
                  {drifted && (
                    <span data-testid={`model-drift-${k}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-50 text-red-700">
                      <AlertTriangle className="h-3 w-3" /> stale override — intended {v.intended_model_key}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          {s.models.has_drift && (
            <div data-testid="model-drift-banner" className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-2 py-1.5">
              A configured model differs from the registry's intended default (a hidden, non-obvious variable). Report to the owner before relying on it; do not change silently.
            </div>
          )}
        </div>
      </div>

      {/* Environment switch — toggles a safety guard */}
      <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-4">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <span className="text-sm font-semibold text-amber-800">Change environment (safety guard)</span>
        </div>
        {s.environment.source_kind === "env_var" ? (
          <p className="text-xs text-amber-700 mb-1" data-testid="env-locked-note">
            The environment is fixed by the deploy variable <b>APP_ENVIRONMENT = {s.environment.deploy_env_value}</b> and is
            authoritative. It <b>cannot be changed here</b> — update the deploy environment variable (or remove it to use a
            persisted setting).
          </p>
        ) : (
          <p className="text-xs text-amber-700 mb-3">
            Setting <b>production</b> enables real outbound email. This change is <b>persisted (durable — survives restart)</b>,
            stamped with who set it, and audited. It applies while no deploy <b>APP_ENVIRONMENT</b> variable is set; if that
            variable is ever set, it takes precedence.
          </p>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <select data-testid="env-select" value={envChoice} onChange={(e) => setEnvChoice(e.target.value)}
            disabled={s.environment.source_kind === "env_var"}
            className="border border-slate-300 rounded-md px-2 py-1.5 text-sm disabled:opacity-40">
            {["production", "preview", "staging", "development"].map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          <label className="flex items-center gap-1.5 text-xs text-slate-700">
            <input data-testid="env-confirm" type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)}
              disabled={s.environment.source_kind === "env_var"} />
            I understand this changes whether real email can be sent
          </label>
          <button data-testid="env-apply" disabled={busy || !confirm || s.environment.source_kind === "env_var" || envChoice === s.environment.value}
            onClick={changeEnv}
            className="px-3 py-1.5 rounded-md text-sm font-medium bg-slate-900 text-white disabled:opacity-40">
            Apply
          </button>
        </div>
      </div>

      {/* Re-run a daily job */}
      <div className="rounded-xl border border-slate-200 p-4 bg-white">
        <div className="text-sm font-semibold text-slate-900 mb-2">Re-run today's Morning Intelligence Briefing</div>
        <div className="text-[11px] text-slate-500 mb-2">Regenerates and re-sends <b>today's</b> briefing from today's data. It does not rebuild a past day.</div>
        <div className="flex flex-wrap items-center gap-3">
          <button data-testid="rerun-preview" disabled={busy} onClick={doPreview}
            className="px-3 py-1.5 rounded-md text-sm font-medium border border-slate-300 text-slate-700 disabled:opacity-40">
            Preview recipients
          </button>
          <button data-testid="rerun-send" disabled={busy} onClick={doSend}
            className="px-3 py-1.5 rounded-md text-sm font-medium bg-blue-600 text-white disabled:opacity-40">
            Re-run &amp; send today
          </button>
        </div>
        {preview && (
          <div data-testid="rerun-preview-result" className="mt-3 text-xs text-slate-600 space-y-1">
            {preview.warning && (
              <div data-testid="rerun-nobody-warning" className="flex items-start gap-1.5 text-red-700 bg-red-50 border border-red-200 rounded-md px-2 py-1.5 font-medium">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {preview.warning}
              </div>
            )}
            <div>Total recipients: <b className={preview.reaches_nobody ? "text-red-700" : "text-slate-800"}>{preview.total_recipients}</b></div>
            <div>Recipients by role: {JSON.stringify(preview.recipients_by_role)}</div>
            <div className="text-amber-700">{preview.note}</div>
          </div>
        )}
      </div>

      {msg && <div data-testid="status-msg" className="text-sm text-slate-800 bg-slate-100 rounded-md px-3 py-2">{msg}</div>}

      {/* Model governance — governed / pinned / not-yet-governed + observed vs configured */}
      {gov && (
        <div className="rounded-xl border border-slate-200 p-4 bg-white" data-testid="model-governance">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold text-slate-900">Model governance</div>
            <button data-testid="model-test-btn" onClick={runModelTest} disabled={testing}
              className="px-3 py-1.5 rounded-md text-xs font-medium border border-slate-300 text-slate-700 disabled:opacity-40">
              {testing ? "Testing…" : "Test this configuration"}
            </button>
          </div>

          {/* Governance coverage — the one-line answer: does this console control the product?
              Counted from status, NOT observation; pinned and not-yet-governed kept separate. */}
          {gov.coverage && (
            <div className="text-xs text-slate-700 mb-3 rounded-lg bg-slate-50 border border-slate-100 px-3 py-2" data-testid="governance-coverage">
              <b className="text-emerald-700">{gov.coverage.governed} of {gov.coverage.total_purposes}</b> purposes governed by this console
              {" · "}<span className="text-slate-500">{gov.coverage.pinned} pinned with a reason</span>
              {" · "}<span className="text-amber-700">{gov.coverage.not_yet_governed} not yet governed</span>
              <div className="text-[10px] text-slate-400 mt-0.5">Coverage means governed, not observed — it does not move because telemetry has seen more.</div>
            </div>
          )}

          {/* Governed tiers with provenance + drift + freshness */}
          <div className="grid sm:grid-cols-3 gap-3 mb-3">
            {Object.entries(gov.governed_tiers || {}).map(([p, v]) => {
              const ls = v.last_seen;
              const ageH = ls ? (Date.now() - new Date(ls).getTime()) / 3.6e6 : null;
              const dot = ls == null ? "bg-slate-300" : (ageH <= 24 ? "bg-emerald-500" : "bg-amber-500");
              const freshText = ls == null
                ? "not observed yet this window"
                : `last seen ${ls.slice(0, 16).replace("T", " ")}${v.last_seen_model ? ` · ${v.last_seen_model}` : ""}`;
              return (
              <div key={p} className="rounded-lg border border-slate-100 p-2.5" data-testid={`gov-tier-${p}`}>
                <div className="text-[10px] uppercase tracking-wide text-slate-400">{p}</div>
                <div className="text-sm font-semibold text-slate-800">{v.label}</div>
                {v.drifted && <div className="text-[10px] text-red-600 mt-0.5">stale override — intended {v.intended_default}</div>}
                <div className="text-[10px] text-slate-400 mt-0.5">set by: {v.provenance?.source}</div>
                <div className="flex items-center gap-1.5 mt-1" data-testid={`gov-freshness-${p}`}>
                  <span className={`inline-block w-2 h-2 rounded-full ${dot} ${ls == null ? "ring-1 ring-slate-400" : ""}`}></span>
                  <span className={`text-[10px] ${ls == null ? "text-slate-500 italic" : "text-slate-500"}`}>{freshText}</span>
                </div>
              </div>
              );
            })}
          </div>

          {/* Test results */}
          {testResult && (
            <div className="mb-3 text-xs" data-testid="model-test-result">
              {testResult.map((r, i) => (
                <div key={i} className={r.ok ? "text-emerald-700" : "text-red-600"}>
                  {r.purpose}: configured <b>{r.configured}</b> → {r.ok ? <>answered <b>{r.answered}</b> {r.match ? "✓ match" : "⚠ MISMATCH"}</> : `failed (${r.error})`}
                </div>
              ))}
            </div>
          )}

          {/* Governance map: governed / pinned / not-yet-governed */}
          <div className="overflow-x-auto mb-3">
            <table className="w-full text-xs" data-testid="governance-map">
              <thead><tr className="text-left text-slate-400">
                <th className="py-1 pr-3">Purpose</th><th className="py-1 pr-3">Console control</th><th className="py-1 pr-3">Model / constant</th><th className="py-1">Note</th>
              </tr></thead>
              <tbody>
                {(gov.governance_map || []).map((r, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="py-1 pr-3 font-medium">{r.purpose}</td>
                    <td className="py-1 pr-3">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                        r.status === "governed" ? "bg-emerald-50 text-emerald-700"
                        : r.status === "pinned" ? "bg-slate-100 text-slate-600"
                        : "bg-amber-50 text-amber-700"}`}>
                        {r.status === "governed" ? "governed by this console"
                          : r.status === "pinned" ? "pinned (reason below)"
                          : "not yet governed"}
                      </span>
                    </td>
                    <td className="py-1 pr-3">{r.value || r.source || "—"}</td>
                    <td className="py-1 text-slate-500">{r.reason || r.note || r.runs || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Observed vs configured */}
          <div className="text-[11px] font-semibold text-slate-600 mb-1">Observed (what actually answered)</div>
          <p className="text-[10px] text-slate-400 mb-1">{gov.observed_note}</p>
          {gov.observation_window && (
            <p className="text-[10px] text-slate-500 mb-1" data-testid="observation-window">
              Observation window: since {(gov.observation_window.since || "").slice(0, 16).replace("T", " ")} → {(gov.observation_window.as_of || "").slice(0, 16).replace("T", " ")}. Unobserved features are <b>unproven</b> until their schedule has cycled.
            </p>
          )}
          {(gov.observed || []).length === 0 ? (
            <div className="text-xs text-slate-500" data-testid="observed-empty">No model call observed yet in this window. Unobserved means <b>unproven</b>, not dead — daily/weekly/upload-triggered jobs may simply not have run yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="observed-table">
                <thead><tr className="text-left text-slate-400">
                  <th className="py-1 pr-3">Feature</th><th className="py-1 pr-3">Purpose</th><th className="py-1 pr-3">Observed model</th><th className="py-1 pr-3">vs configured</th><th className="py-1 pr-3">×</th><th className="py-1">Last seen</th>
                </tr></thead>
                <tbody>
                  {gov.observed.map((o, i) => {
                    const tier = (gov.governed_tiers || {})[o.purpose];
                    const want = tier ? String(tier.configured_model || "").split("/").pop() : null;
                    const got = o.last_observed_model || "";
                    const mismatch = want && got && !got.includes(want);
                    return (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="py-1 pr-3 max-w-[240px] truncate">{o.feature}</td>
                      <td className="py-1 pr-3 text-slate-500">{o.purpose || "—"}</td>
                      <td className="py-1 pr-3 font-medium">
                        {got || "—"}
                        {got && o.observed_verified === false && (
                          <span className="ml-1 text-[9px] font-normal text-slate-400" title="chat path does not return the answering model; this is the requested model">requested</span>
                        )}
                      </td>
                      <td className="py-1 pr-3" data-testid={`observed-match-${i}`}>
                        {!want ? <span className="text-slate-400">—</span>
                          : mismatch ? <span className="text-red-600 font-semibold">⚠ MISMATCH (configured {want})</span>
                          : <span className="text-emerald-700">✓ matches configured</span>}
                      </td>
                      <td className="py-1 pr-3">{o.observation_count}</td>
                      <td className="py-1 text-slate-500">{(o.last_observed_at || "").slice(0, 16).replace("T", " ")}</td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Send log */}
      <div className="rounded-xl border border-slate-200 p-4 bg-white">
        <div className="text-sm font-semibold text-slate-900 mb-3">Email send log</div>
        {log.length === 0 ? (
          <div className="text-xs text-slate-500">No send events recorded yet in this process.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="send-log-table">
              <thead><tr className="text-left text-slate-400">
                <th className="py-1 pr-3">When</th><th className="py-1 pr-3">Outcome</th>
                <th className="py-1 pr-3">Recipients</th><th className="py-1 pr-3">Subject</th><th className="py-1">Reason</th>
              </tr></thead>
              <tbody>
                {log.map((e, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="py-1 pr-3 whitespace-nowrap">{(e.at || "").slice(0, 19).replace("T", " ")}</td>
                    <td className="py-1 pr-3">
                      {e.outcome === "sent" ? <span className="text-emerald-700 font-medium">sent</span>
                        : e.outcome === "redirected" ? <span className="text-blue-700 font-medium">redirected</span>
                        : <span className="text-amber-700 font-medium">suppressed</span>}
                    </td>
                    <td className="py-1 pr-3">{e.recipient_count}</td>
                    <td className="py-1 pr-3 max-w-[200px] truncate">{e.subject}</td>
                    <td className="py-1 text-slate-500 max-w-[280px] truncate">{e.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
